import type { RequestPayload } from "@inertiajs/core";
import { usePage } from "@inertiajs/react";
import { Divider, Fieldset, Group, Stack, Text, TextInput } from "@mantine/core";
import { MIME_TYPES } from "@mantine/dropzone";
import { useForm } from "@mantine/form";
import { calculateFileSize } from "../../helpers/fileHelper";
import { getFullName } from "../../helpers/stringHelper";
import type { Coach } from "../../models/coach";
import type { Signature } from "../../models/signature";
import { SaveButton } from "../button/SaveButton";
import { DropZone } from "../dropzone/Dropzone";
import { InfoCardPopover } from "../infoCardPopover/InfoCardPopover";
import { FilterSelect } from "../select/FilterSelect";

type SignatureForm = {
  signature?: Signature;
  coaches: Coach[];
  saveButtonLabel: string;
  onSubmit: (values: RequestPayload) => void;
};

type FormErrors = {
  first_name?: string[];
  last_name?: string[];
  signature?: string[];
  email?: string[];
  position?: string[];
};

export function SignatureForm({ signature, coaches, saveButtonLabel, onSubmit }: SignatureForm) {
  const form = useForm({
    mode: "controlled",
    initialValues: {
      first_name: signature?.first_name ?? "",
      last_name: signature?.last_name ?? "",
      user_id: signature?.user_id?.toString(),
      signature: signature?.signature,
      email: signature?.email,
      position: signature?.position,
    },
  });

  const { errors } = usePage<{ errors: FormErrors }>().props;

  form.watch("user_id", ({ value }) => {
    if (value) {
      const coach = coaches.find((coach) => coach.user.id.toString() === value);
      if (coach) {
        form.setFieldValue("first_name", coach.first_name ?? "");
        form.setFieldValue("last_name", coach.last_name ?? "");
        form.setFieldValue("email", coach.user.email);
      }
    } else {
      form.setFieldValue("first_name", "");
      form.setFieldValue("last_name", "");
      form.setFieldValue("email", "");
    }
  });

  const selectedFile = (
    <>
      {form.values.signature && (
        <Text>
          <b>{form.values.signature?.name}</b> {calculateFileSize(form.values.signature?.size)}
        </Text>
      )}
    </>
  );

  const coachLabel = (
    <Group align="center" gap="xs">
      Coach
      <InfoCardPopover>Optionale Kopplung mit einem Coach</InfoCardPopover>
    </Group>
  );

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Fieldset legend="Details">
        <Stack maw="60rem">
          <FilterSelect
            clearable
            label={coachLabel}
            data={coaches.map((coach) => ({
              value: coach.user.id.toString(),
              label: getFullName(coach),
            }))}
            key={form.key("user_id")}
            {...form.getInputProps("user_id")}
          />
          <TextInput
            withAsterisk
            disabled={!!form.getValues().user_id}
            label="Vorname"
            key={form.key("first_name")}
            {...form.getInputProps("first_name")}
            error={errors?.first_name}
          />
          <TextInput
            withAsterisk
            disabled={!!form.getValues().user_id}
            label="Nachname"
            key={form.key("last_name")}
            {...form.getInputProps("last_name")}
            error={errors?.last_name}
          />
          <TextInput
            disabled={!!form.getValues().user_id}
            label="E-Mail"
            key={form.key("email")}
            {...form.getInputProps("email")}
            type="email"
            error={errors?.email}
          />
          <TextInput label="Position" key={form.key("position")} {...form.getInputProps("position")} error={errors?.position} />
          <DropZone
            mimeTypes={[MIME_TYPES.jpeg, MIME_TYPES.png]}
            onDrop={(files) => form.setFieldValue("signature", files[0])}
            multiple={false}
            error={errors?.signature}
          />
          {selectedFile}
          <Divider />
          <SaveButton label={saveButtonLabel} />
        </Stack>
      </Fieldset>
    </form>
  );
}
